import { ReactionFragmentFragment, ReactionName, useUpdateReaction } from '@lopo-tech/lopo-api'
import classNames from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import { CLASS_PREFIX } from 'src/constants/'
import { REACTIONS_TIMEOUT } from 'src/constants/timeouts'
import { translateReactions } from 'src/utils/reactions'
import './reactions.scss'

export interface IReactionsProps {
  reactableId: string;
  reactions: ReactionFragmentFragment;
  onClose: () => void;
  canReact: boolean;
}

interface IReactionProps {
  name: ReactionName;
  reactions: ReactionFragmentFragment;
  onClose?: () => void;
  reactableId: string;
  canReact: boolean;
}

const cls = CLASS_PREFIX + 'reaction'
const wrapperCls = CLASS_PREFIX + 'reactions'

const getReactionClass = (active: boolean, name: ReactionName): string => {
  const reactionClass = active ? `${cls} ${cls}--active` : cls
  const reactionNameClass = `${cls}--${name}`
  const reactionNameActiveClass = active
    ? `${reactionNameClass}--active`
    : reactionNameClass
  return classNames(reactionClass, reactionNameActiveClass)
}

const Reaction: React.FC<IReactionProps> = ({ name, reactions, onClose, reactableId, canReact }) => {
  const timeoutId = useRef<NodeJS.Timeout>()
  const isOwnReaction = reactions.ownReaction === name
  const [active, setActive] = useState(false)

  const [updateReaction] = useUpdateReaction()

  const handleSelect = () => {
    // Without a profile the click belongs to the sign up prompt around this picker, and it
    // reaches it by bubbling. While the confirmation runs, a second press would toggle the
    // reaction off again, which a held Enter key would do on its own.
    if (!canReact || active) {
      return
    }
    setActive(true)

    updateReaction({
      variables: {
        input: {
          reactableId,
          reactionName: name
        }
      }
    })
    timeoutId.current = setTimeout(() => {
      setActive(false)
      onClose?.()
    }, REACTIONS_TIMEOUT)
  }

  useEffect(() => {
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current)
      }
    }
  }, [])

  const reactionClasses = getReactionClass(active, name)
  const label = translateReactions(name)

  // The sticker carries no text, so the name reaches assistive tech through aria-label
  // and the pointer through title. A button brings Enter and Space with it, and both end up
  // in the same click the mouse produces.

  return (
    <button
      aria-label={label}
      aria-pressed={isOwnReaction}
      className={cls + '__wrapper'}
      onClick={handleSelect}
      title={label}
      type="button"
    >
      <div className={reactionClasses} />
    </button>
  )
}

const Reactions: React.FC<IReactionsProps> = ({
  reactions,
  reactableId,
  onClose,
  canReact
}) => {
  return (
    <div className={classNames(wrapperCls)}>
      {reactions.availableReactions.map((reaction) => (
        <Reaction
          key={reaction}
          name={reaction}
          reactableId={reactableId}
          reactions={reactions}
          onClose={onClose}
          canReact={canReact}
        />
      ))}
    </div>
  )
}

export default Reactions
